import React, { useState } from 'react';
import './MessageRating.css';

interface MessageRatingProps {
  messageIndex: number;
  conversationId: string;
  existingRating?: boolean;
  onRatingSubmitted?: () => void;
}

export const MessageRating: React.FC<MessageRatingProps> = ({
  messageIndex,
  conversationId,
  existingRating,
  onRatingSubmitted,
}) => {
  const [rating, setRating] = useState<boolean | null>(existingRating ?? null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(existingRating !== undefined);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleThumbClick = (isPositive: boolean) => {
    if (isSubmitted) return;
    setRating(isPositive);
    setShowFeedback(true);
  };

  const handleSubmit = async () => {
    if (rating === null) {
      setSubmitMessage('Please select thumbs up or down');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0x902r3pk4.execute-api.ap-south-1.amazonaws.com/dev';
      const response = await fetch(`${apiUrl}/chat/rating`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversationId,
          messageIndex,
          isPositive: rating,
          feedback: feedback.trim() || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit rating');
      }

      setIsSubmitted(true);
      setShowFeedback(false);
      setSubmitMessage('Thank you for your feedback!');
      
      if (onRatingSubmitted) {
        onRatingSubmitted();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSubmitMessage(''), 3000);
    } catch (error) {
      console.error('Error submitting rating:', error);
      setSubmitMessage('Failed to submit rating. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowFeedback(false);
    setFeedback('');
    setRating(null);
  };

  return (
    <div className="message-rating">
      <div className="rating-header">
        <span className="rating-label">Was this response helpful?</span>
      </div>
      
      <div className="thumbs-container">
        <button
          className={`thumb-btn thumb-up ${rating === true ? 'selected' : ''} ${isSubmitted ? 'submitted' : ''}`}
          onClick={() => handleThumbClick(true)}
          disabled={isSubmitted}
          aria-label="Thumbs up"
        >
          👍
        </button>
        <button
          className={`thumb-btn thumb-down ${rating === false ? 'selected' : ''} ${isSubmitted ? 'submitted' : ''}`}
          onClick={() => handleThumbClick(false)}
          disabled={isSubmitted}
          aria-label="Thumbs down"
        >
          👎
        </button>
      </div>

      {showFeedback && !isSubmitted && (
        <div className="feedback-section">
          <textarea
            className="feedback-textarea"
            placeholder={rating ? "What did you like about this response?" : "How can we improve? (e.g., too long, not clear, inaccurate, etc.)"}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
          />
          <div className="feedback-actions">
            <button
              className="btn-submit-rating"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
            <button
              className="btn-cancel-rating"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {submitMessage && (
        <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
          {submitMessage}
        </div>
      )}
    </div>
  );
};
